import {Icon} from "@client/components/Icon";
import {TouchHandler} from "@client/components/TouchHandler";
import * as dates from "@client/dates";
import type {Locator} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import * as analytics from "@client/shared/analytics";
import NumberFlow, {NumberFlowGroup} from "@number-flow/react";
import {classNames} from "@reactivated";
import React from "react";

const styles = {
    dateCarousel: "flex justify-center items-center h-10",
    main: "flex-1 h-full flex justify-center items-center p-0",
    link: "flex items-center h-full",
};

interface Props {
    previousDayLink: Locator | string | null;
    onPreviousClick?: () => void;
    onDateClick: () => void;
    nextDayLink: Locator | string | null;
    date: DateValue;
    // Inside a surface that paints its own background (SimpleScene's glass).
    bare?: boolean;
    restTimer?: {
        seconds: number | null;
        setRestTimer: (seconds: number | null) => void;
    } | null;
}

function Countdown({seconds}: {seconds: number}) {
    const hh = Math.floor(seconds / 3600);
    const mm = Math.floor((seconds % 3600) / 60);
    const ss = seconds % 60;
    return (
        <NumberFlowGroup>
            <div
                style={
                    {
                        fontVariantNumeric: "tabular-nums",
                        "--number-flow-char-height": "0.85em",
                    } as React.CSSProperties
                }
                className="flex items-baseline font-semibold"
            >
                <NumberFlow trend={-1} value={hh} format={{minimumIntegerDigits: 2}} />
                <NumberFlow
                    prefix=":"
                    trend={-1}
                    value={mm}
                    digits={{1: {max: 5}}}
                    format={{minimumIntegerDigits: 2}}
                />
                <NumberFlow
                    prefix=":"
                    trend={-1}
                    value={ss}
                    digits={{1: {max: 5}}}
                    format={{minimumIntegerDigits: 2}}
                />
            </div>
        </NumberFlowGroup>
    );
}

function DayLink(props: {
    label: string;
    className: string;
    onClick?: () => void;
    link: Props["previousDayLink"];
    children: React.ReactNode;
}) {
    return (
        <TouchHandler
            haptic
            ariaLabel={props.label}
            className={props.className}
            onClick={props.onClick}
            link={props.link}
        >
            {props.children}
        </TouchHandler>
    );
}

export const DateCarousel = (props: Props) => {
    const parsedDate = dates.parseISO(props.date);

    const {dateFormat, dateClasses} =
        parsedDate.getFullYear() !== new Date().getFullYear()
            ? {
                  dateFormat: "eee, MMM do yyyy",
                  dateClasses: "text-[0.8rem] sm:text-sm font-medium w-34 sm:w-48",
              }
            : {
                  dateFormat: "eee, MMM do",
                  dateClasses: "text-sm font-normal w-[6.6rem] sm:w-36 pl-1",
              };

    const carouselBaseClasses = styles.dateCarousel;
    const background = props.bare === true ? null : "bg-white";
    const mainBaseClasses = classNames(styles.main, background);

    const restTimer = props.restTimer;
    const restTimerClass =
        restTimer?.seconds != null ? "bg-red-500! text-white! rounded-full" : "";
    const activeRestTimerClasses = restTimer?.seconds != null ? "invisible" : "";
    const iconRestingClass = restTimer?.seconds != null ? "text-white" : "";

    return (
        <div
            data-day={props.date}
            className={classNames(carouselBaseClasses, restTimerClass, "relative")}
        >
            <div
                className={classNames(
                    "contents",
                    restTimer?.seconds == null && "invisible pointer-events-none",
                )}
            >
                <button
                    className="absolute left-3 flex items-center justify-center text-sm font-medium text-white"
                    onClick={() => {
                        if (restTimer == null) return;
                        hardware.haptic();
                        void analytics.track("POSTPONED_REST_TIMER");
                        restTimer.setRestTimer((restTimer.seconds ?? 0) + 15);
                    }}
                >
                    +15
                </button>
                <div className="absolute flex items-center justify-center text-center text-white">
                    <Countdown seconds={restTimer?.seconds ?? 0} />
                </div>
                <button
                    className="absolute right-3 flex items-center justify-center text-sm font-medium text-white"
                    onClick={() => {
                        if (restTimer == null) return;
                        hardware.haptic();
                        void analytics.track("SKIPPED_REST_TIMER");
                        restTimer.setRestTimer(null);
                    }}
                >
                    Skip
                </button>
            </div>
            <DayLink
                label="Previous day"
                className={classNames(
                    "pl-3 pr-1",
                    styles.link,
                    background,
                    activeRestTimerClasses,
                    "rounded-l-full",
                )}
                onClick={props.onPreviousClick}
                link={props.previousDayLink}
            >
                <Icon
                    large="small"
                    icon="custom-chevron-left"
                    className={iconRestingClass}
                />
            </DayLink>
            <TouchHandler
                className={classNames(mainBaseClasses, activeRestTimerClasses)}
                onClick={props.onDateClick}
            >
                <div className="">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="black"
                        className="relative -mt-px size-[1.20rem]"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
                        />
                    </svg>
                </div>
                {/* Approximately longest date: 2025-05-28 */}
                <span className={classNames("text-center truncate", dateClasses)}>
                    {dates.format(parsedDate, dateFormat)}
                </span>
            </TouchHandler>
            <DayLink
                label="Next day"
                className={classNames(
                    "pl-1 pr-3",
                    styles.link,
                    background,
                    activeRestTimerClasses,
                    "rounded-r-full",
                )}
                link={props.nextDayLink}
            >
                <Icon
                    large="small"
                    icon="custom-chevron-right"
                    className={iconRestingClass}
                />
            </DayLink>
        </div>
    );
};
